<script setup lang="ts">
import ProductImage from "@/product/components/ProductImage.vue";
import BaseCard from "@/core/components/base/BaseCard.vue";
import LaboratoryProductQuantityStepper from "@/laboratory/components/LaboratoryProductQuantityStepper.vue";
import ProductPriceLine from "@/sellin-offer/components/ProductPriceLine.vue";
import LaboratoryProduct from "@/core/interfaces/laboratory/LaboratoryProduct";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {useCartStore} from "@/laboratory/stores/cart.store";
import {computed, onUnmounted, ref} from "vue";
import {storeToRefs} from "pinia";
import {colors} from "@/theme/colors";

const ADDED_LABEL_DURATION = 5000;

const {need, quantity} = defineProps<{
    need: LaboratoryProduct;
    color?: string;
    quantityNeeded: number;
    secondaryColor?: string;
    quantity?: number;
}>();

const emit = defineEmits(['on-update']);

const {cart, franco} = storeToRefs(useCartStore());
const localQuantity = ref(quantity ?? 0);
const justAdded = ref(false);
let addedLabelTimeout: ReturnType<typeof setTimeout> | undefined;

const available = computed(() => {
    return need.depositaryAvailability ? need.depositaryAvailability.available : true;
});

const quantityInCart = computed(() => {
    return cart.value?.lines?.find((line) => line.product.id === need.id)?.quantity || 0;
});

/**
 * Add the selected quantity to the cart, then reset the stepper and display
 * the confirmation label in place of the button for a few seconds.
 */
const addToCart = () => {
    emit('on-update', localQuantity.value);
    localQuantity.value = 0;
    justAdded.value = true;

    clearTimeout(addedLabelTimeout);
    addedLabelTimeout = setTimeout(() => {
        justAdded.value = false;
    }, ADDED_LABEL_DURATION);
};

onUnmounted(() => {
    clearTimeout(addedLabelTimeout);
});

/**
 * Get the label to display depending on the availability.
 *
 * @return {string}
 */
const getAvailabilityLabel = (available?: boolean): string => {
    switch (available) {
        case true:
            return "Disponible";
        case false:
            return "Indisponible";
        default:
            return "Non renseigné";
    }
};

/**
 * Get availability chip color.
 */
const availabilityChipColor = (available?: boolean): string => {
    return available === undefined
        ? 'transparent'
        : available
            ? colors.secondary.DEFAULT
            : colors.red.DEFAULT;
};

/**
 * Get availability chip border color.
 */
const availabilityBorderColor = (_available?: boolean): string => {
    return _available === undefined
        ? colors.gray.DEFAULT
        : _available
            ? colors.secondary.DEFAULT
            : colors.red.DEFAULT;
};
</script>

<template>
  <BaseCard
    class="mx-5"
  >
    <div class="flex flex-row p-4 gap-2">
      <ProductImage :product="need" :size="28" />
      <div class="flex flex-1 flex-col">
        <div class="flex flex-row justify-between">
          <div class="flex flex-col items-start">
            <div class="flex flex-row justify-between">
              <p class="font-semibold">
                {{ need.name }}
              </p>
            </div>
            <p class="text-gray-500">
              CIP {{ need.cip13 }}
            </p>
            <div>
              <p v-if="need?.order_comment" class="text-gray-700">
                {{ need.order_comment }}
              </p>
              <p v-else-if="need?.has_order_quota" class="text-gray-700">
                Produit sous quota
              </p>
            </div>
            <div class="mt-2">
              <ProductPriceLine :product="need" unit="u" color="black" />
            </div>
          </div>
          <div v-if="franco" class="flex flex-col items-end justify-end min-w-[160px] gap-2 min-h-[90px]">
            <Transition name="added" mode="out-in">
              <p v-if="justAdded" class="text-primary font-semibold whitespace-nowrap">
                Ajouté au panier ✅
              </p>
              <base-button
                v-else-if="localQuantity"
                :color="color"
                :style="{'color': secondaryColor}"
                @click="addToCart"
              >
                Ajouter {{ localQuantity }} au panier
              </base-button>
            </Transition>
            <p v-if="quantityInCart < quantityNeeded" class="text-red pt-1">
              {{ quantityInCart }} dans le panier
            </p>
            <LaboratoryProductQuantityStepper
              v-model="localQuantity"
              :min-quantity="need.laboratoryShopPrice.minimumQuantity"
              :color="color"
              :secondary-color="secondaryColor"
              :packing-quantity="need.laboratoryShopPrice.packingQuantity"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-row justify-between bg-gray-200 p-2 px-4">
      <div>
        <p>
          Stock <span class="font-semibold mr-4">{{ need.needs?.quantityInStock || 0 }}</span>
          <span v-if="!!need.needs?.monthlySales">
            Ventes/mois <span class="font-semibold mr-4">{{ need.needs?.monthlySales || 0 }}</span>
          </span>
          Besoin <span class="font-semibold mr-4">{{ need.needs?.quantityNeeded || 0 }}</span>
        </p>
      </div>
      <div class="flex flex-row items-center gap-1">
        <div
          class="w-3 h-3 rounded-full border-2 border-gray-400"
          :style="{
            'background-color': availabilityChipColor(available),
            'border-color': availabilityBorderColor(available)
          }"
        />
        <p>{{ getAvailabilityLabel(available) }}</p>
      </div>
    </div>
  </BaseCard>
</template>

<style scoped>
.added-enter-active {
  transition: opacity 0.25s ease-out, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.added-leave-active {
  transition: opacity 0.15s ease-in, transform 0.15s ease-in;
}

.added-enter-from, .added-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(4px);
}
</style>
